import type { SmartScheduleEvent } from '../smartSchedule/types';
import { toSmartScheduleKst } from '../smartSchedule/date';
import type { ModifyGeneralScheduleTimeBody, ModifyOneOnOneScheduleTimeBody } from '../../api/schedule';
import { QuickMoveSavePlan, QuickMoveSource, QuickMoveVerification } from './types';

/** 검증을 통과한 시트 값으로 저장 계획을 만든다. */
export const buildQuickMoveSavePlan = (
    source: QuickMoveSource,
    target: { startTime: string; endTime: string }
): QuickMoveSavePlan => ({
    source,
    targetStartTime: target.startTime,
    targetEndTime: target.endTime,
});

const sameInstant = (left: string, right: string): boolean =>
    toSmartScheduleKst(left).valueOf() === toSmartScheduleKst(right).valueOf();

/** 목표가 원래와 같은가(같으면 요청하지 않는다). */
export const isQuickMovePlanUnchanged = (plan: QuickMoveSavePlan): boolean =>
    sameInstant(plan.targetStartTime, plan.source.startTime) && sameInstant(plan.targetEndTime, plan.source.endTime);

/**
 * PT 요청 본문. expected* 는 시트를 연 시점의 원본이다.
 * expectedAttendanceStatus 는 원본 raw 값이 null/없음이면 생략(§6-2).
 */
export const buildPrivateQuickMoveBody = (plan: QuickMoveSavePlan): ModifyOneOnOneScheduleTimeBody => {
    const body: ModifyOneOnOneScheduleTimeBody = {
        coachId: plan.source.coachId,
        changedStartTime: plan.targetStartTime,
        changedEndTime: plan.targetEndTime,
        expectedStartTime: plan.source.startTime,
        expectedEndTime: plan.source.endTime,
    };
    if (plan.source.attendanceStatusRaw) body.expectedAttendanceStatus = plan.source.attendanceStatusRaw;
    return body;
};

/** 일반일정 요청 본문. name 은 시트를 연 시점의 원래 제목(§6-4, last-write-wins 수용). */
export const buildGeneralQuickMoveBody = (plan: QuickMoveSavePlan): ModifyGeneralScheduleTimeBody => ({
    coachId: plan.source.coachId,
    name: plan.source.generalName,
    startTime: plan.targetStartTime,
    endTime: plan.targetEndTime,
});

/** 재조회해야 하는 KST 날짜(원래 날·목적지 날, 중복 제거). */
export const getQuickMoveVerificationDates = (plan: QuickMoveSavePlan): string[] =>
    Array.from(
        new Set([
            toSmartScheduleKst(plan.source.startTime).format('YYYY-MM-DD'),
            toSmartScheduleKst(plan.targetStartTime).format('YYYY-MM-DD'),
        ])
    );

const matchesTimes = (event: SmartScheduleEvent, startTime: string, endTime: string): boolean =>
    sameInstant(event.startTime, startTime) && sameInstant(event.endTime, endTime);

/** PT 교체로 생긴 새 일정. 같은 조건의 별도 일정까지 숨기지 않도록 한 건만 찾는다. */
export const findQuickMoveReplacement = (plan: QuickMoveSavePlan, events: SmartScheduleEvent[]): SmartScheduleEvent | undefined =>
    plan.source.kind === 'private' ? events.find(
        (event) =>
            event.kind === 'private' &&
            event.id !== plan.source.scheduleId &&
            event.coachId === plan.source.coachId &&
            event.memberIds.includes(plan.source.memberId) &&
            matchesTimes(event, plan.targetStartTime, plan.targetEndTime)
    ) : undefined;

/**
 * 재조회한 이벤트(원래 날 + 목적지 날)로 실제 반영 여부를 판정한다.
 *
 * - PT 는 교체(신규 생성 → 원본 취소)라 id 가 바뀐다. 원본 id 가 사라지고 같은 회원·강사의
 *   PT 가 목표 시각에 있으면 applied, 원본이 그대로면 notApplied, 둘 다 있으면 bothPresent(D4).
 * - 일반일정은 제자리 수정이라 같은 id 의 시각으로 판정한다. 사라졌으면 unknown.
 */
export const verifyQuickMoveApplied = (plan: QuickMoveSavePlan, events: SmartScheduleEvent[]): QuickMoveVerification => {
    const { source } = plan;
    const original = events.find((event) => event.id === source.scheduleId);

    if (source.kind === 'general') {
        if (!original) return 'unknown';
        if (matchesTimes(original, plan.targetStartTime, plan.targetEndTime)) return 'applied';
        if (matchesTimes(original, source.startTime, source.endTime)) return 'notApplied';
        return 'unknown';
    }

    const replacement = findQuickMoveReplacement(plan, events);
    if (replacement && original) return 'bothPresent';
    if (replacement) return 'applied';
    if (original) return 'notApplied';
    return 'unknown';
};
